import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Box, Divider, IconButton, Tooltip, Typography } from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import FitScreenIcon from '@mui/icons-material/FitScreen';
import { PV_FONT_SIZE, PV_LINE_SPACING, PV_LOGO_SVG, PV_PAGE } from '../../utils/pvDocument';

/*
 * Aperçu A4 du PV : rend les nœuds de utils/pvDocument.js (les mêmes que l'export Word)
 * avec les dimensions, marges, polices et espacements du .docx, puis les répartit
 * en pages comme Word : en-tête répété, coupure entre deux lignes, titre de rubrique
 * gardé avec la ligne qui le suit, espace avant supprimé en haut de page.
 */

// 96 dpi : 1 px = 15 twips, 1 demi-point = 2/3 px
export const twips = (value) => (value || 0) / 15;
export const halfPoints = (value) => (value * 2) / 3;

// Hauteur d'une ligne « simple » de Word en Helvetica/Arial, en multiple de la taille de police
const WORD_SINGLE_LINE = 1.15;
export const lineHeight = (line = PV_LINE_SPACING) => (line / 240) * WORD_SINGLE_LINE;

// En justifié, Word resserre les espaces jusqu'à 80 % pour faire tenir un mot de plus (espace Arial : 0,278 em)
const JUSTIFY_WORD_SPACING = '-0.0556em';

// Sous une image en ligne, Word ne garde que la descente de la police (Arial : 434/2048 em)
const FONT_DESCENT = 434 / 2048;

// Le .docx ne déclare ni style ni marges de cellule : Word applique alors 0,5 pt à gauche et à droite
const CELL_PADDING = twips(10);

// Les filets de cellule (1/8 pt) n'occupent qu'environ 0,25 pt par rangée dans Word
const CELL_RULE = twips(5);

export const PAGE = {
  width: twips(PV_PAGE.width),
  height: twips(PV_PAGE.height),
  top: twips(PV_PAGE.margin.top),
  bottom: twips(PV_PAGE.margin.bottom),
  left: twips(PV_PAGE.margin.left),
  right: twips(PV_PAGE.margin.right),
  header: twips(PV_PAGE.margin.header),
};
export const CONTENT_WIDTH = PAGE.width - PAGE.left - PAGE.right;
const PAGE_GAP = 24;

const LOGO_SRC = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(PV_LOGO_SVG)}`;

const ZOOM_STEPS = [0.5, 0.67, 0.75, 0.9, 1, 1.1, 1.25, 1.5, 1.75, 2];
const FIT_MAX = 1.25;
const ZOOM_STORAGE_KEY = 'pvPreviewZoom';

const EPS = 0.5;

const PAPER_TEXT = {
  boxSizing: 'border-box',
  fontFamily: 'Helvetica, Arial, sans-serif',
  fontSize: halfPoints(PV_FONT_SIZE.body),
  fontWeight: 400,
  fontStyle: 'normal',
  lineHeight: lineHeight(),
  color: '#000',
  textAlign: 'left',
  textTransform: 'none',
  letterSpacing: 'normal',
  wordSpacing: 'normal',
  fontKerning: 'none',
  whiteSpace: 'pre-wrap',
  overflowWrap: 'break-word',
};

const TEXT_ALIGN = { left: 'left', center: 'center', right: 'right', justify: 'justify' };

// Bordure Word : taille en huitièmes de point, espace en points
const borderWidth = (border) => Math.max(1, border.size / 6);
const borderSpace = (border) => ((border.space || 0) * 4) / 3;
const borderCss = (border) => `${borderWidth(border)}px solid #${border.color}`;

function PvRun({ run, ctx }) {
  if (run.break) return <br />;

  const text = run.field ? String(run.field === 'page' ? ctx.page.number : ctx.page.count) : run.text;
  if (!text) return null;

  const decorations = [run.underline && 'underline', run.strike && 'line-through'].filter(Boolean);
  return (
    <span
      style={{
        fontSize: halfPoints(run.size ?? PV_FONT_SIZE.body),
        fontWeight: run.bold ? 700 : undefined,
        fontStyle: run.italics ? 'italic' : undefined,
        textDecoration: decorations.length ? decorations.join(' ') : undefined,
        color: run.color ? `#${run.color}` : undefined,
        backgroundColor: run.highlight ? `#${run.highlight}` : undefined,
      }}
    >
      {text}
    </span>
  );
}

function PvParagraph({ node, ctx }) {
  const spacing = node.spacing || {};
  const indent = node.indent || {};
  const { top, bottom, left } = node.border || {};
  const runs = node.runs || [];

  // Comme dans Word, un paragraphe vide ou terminé par un saut de ligne occupe une ligne
  const hasContent = runs.some((run) => run.break || run.field || run.text);
  const endsWithBreak = runs.length > 0 && runs[runs.length - 1].break;

  // Dans Word, la hauteur d'une ligne ne dépend que du texte qu'elle porte : la police du bloc
  // (ligne minimale en CSS) prend la plus petite taille du paragraphe
  const sizes = runs.filter((run) => run.text || run.field).map((run) => run.size ?? PV_FONT_SIZE.body);
  const strutSize = sizes.length ? Math.min(...sizes) : PV_FONT_SIZE.body;

  // Une bordure gauche se dessine à gauche du texte, qui reste aligné sur le retrait
  const leftOffset = left ? borderWidth(left) + borderSpace(left) : 0;

  return (
    <div
      style={{
        paddingTop: twips(spacing.before),
        paddingBottom: twips(spacing.after),
        paddingLeft: Math.max(0, twips(indent.left) - leftOffset),
      }}
    >
      <div
        data-pv-keep-top={top ? 'true' : undefined}
        style={{
          textAlign: TEXT_ALIGN[node.alignment] || 'left',
          textIndent: indent.hanging ? -twips(indent.hanging) : undefined,
          wordSpacing: node.alignment === 'justify' ? JUSTIFY_WORD_SPACING : undefined,
          fontSize: halfPoints(strutSize),
          lineHeight: lineHeight(spacing.line),
          borderTop: top ? borderCss(top) : undefined,
          paddingTop: top ? borderSpace(top) : undefined,
          borderBottom: bottom ? borderCss(bottom) : undefined,
          paddingBottom: bottom ? borderSpace(bottom) : undefined,
          borderLeft: left ? borderCss(left) : undefined,
          paddingLeft: left ? borderSpace(left) : undefined,
        }}
      >
        {runs.map((run, index) => <PvRun key={index} run={run} ctx={ctx} />)}
        {(!hasContent || endsWithBreak) && '\u200b'}
      </div>
    </div>
  );
}

function PvImage({ node, ctx }) {
  const src = node.logo ? LOGO_SRC : node.src;

  if (!node.logo && ctx.failedImages.has(src)) {
    return (
      <PvParagraph
        node={{
          alignment: node.alignment,
          runs: [{ text: `[Image non disponible: ${src}]`, italics: true, color: '999999' }],
        }}
        ctx={ctx}
      />
    );
  }

  return (
    <div
      style={{
        textAlign: TEXT_ALIGN[node.alignment] || 'left',
        lineHeight: 0,
        paddingBottom: halfPoints(PV_FONT_SIZE.body) * FONT_DESCENT,
      }}
    >
      <img
        src={src}
        alt=""
        width={node.width}
        height={node.height}
        style={{ display: 'inline-block', width: node.width, height: node.height, verticalAlign: 'baseline' }}
        onError={node.logo ? undefined : () => ctx.onImageError(src)}
      />
    </div>
  );
}

function PvTable({ node, ctx }) {
  const hasWidths = node.rows.some((row) => row.cells.some((cell) => cell.width));

  return (
    <table
      style={{
        width: '100%',
        borderCollapse: 'collapse',
        borderSpacing: 0,
        tableLayout: hasWidths ? 'fixed' : 'auto',
        boxShadow: node.borders ? 'inset -1px -1px 0 #000' : undefined,
      }}
    >
      <tbody>
        {node.rows.map((row, rowIndex) => (
          <tr key={rowIndex}>
            {row.cells.map((cell, cellIndex) => (
              <td
                key={cellIndex}
                data-pv-keep-top={node.borders ? 'true' : undefined}
                style={{
                  boxSizing: 'border-box',
                  width: cell.width ? `${cell.width}%` : undefined,
                  verticalAlign: cell.verticalAlign === 'center' ? 'middle' : 'top',
                  padding: `${node.borders ? CELL_RULE : 0}px ${CELL_PADDING}px 0`,
                  border: 'none',
                  boxShadow: node.borders ? 'inset 1px 1px 0 #000' : undefined,
                }}
              >
                <PvNodes nodes={cell.children} ctx={ctx} />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function PvNode({ node, ctx }) {
  if (node.type === 'table') return <PvTable node={node} ctx={ctx} />;
  if (node.type === 'image') return <PvImage node={node} ctx={ctx} />;
  return <PvParagraph node={node} ctx={ctx} />;
}

function PvNodes({ nodes, ctx }) {
  return nodes.map((node, index) => <PvNode key={index} node={node} ctx={ctx} />);
}

const FLOW_CTX = { page: { number: 1, count: 1 }, failedImages: new Set(), onImageError: () => {} };

// Nœuds du PV rendus d'un seul tenant, sans pagination (introduction et clôture de l'éditeur)
export function PvFlowText({ nodes, color = '#000', ...props }) {
  const collapsed = useMemo(() => collapseSpacing(nodes), [nodes]);
  return (
    <div {...props} style={{ ...PAPER_TEXT, color }}>
      <PvNodes nodes={collapsed} ctx={FLOW_CTX} />
    </div>
  );
}

// Word ne cumule pas l'espace après d'un paragraphe et l'espace avant du suivant : seul le plus grand compte
const collapseSpacing = (nodes) => {
  let previousAfter = 0;
  return nodes.map((node) => {
    if (node.type === 'table') {
      previousAfter = 0;
      return {
        ...node,
        rows: node.rows.map((row) => ({
          ...row,
          cells: row.cells.map((cell) => ({ ...cell, children: collapseSpacing(cell.children) })),
        })),
      };
    }
    if (node.type !== 'paragraph') {
      previousAfter = 0;
      return node;
    }
    const before = node.spacing?.before || 0;
    const collapsed = before && previousAfter
      ? { ...node, spacing: { ...node.spacing, before: Math.max(0, before - previousAfter) } }
      : node;
    previousAfter = node.spacing?.after || 0;
    return collapsed;
  });
};

/*
 * Tranches verticales d'un bloc qu'une coupure de page ne peut pas traverser :
 * boîtes de ligne du texte, images, et filet supérieur collé à sa première ligne.
 * Les positions sont relatives au haut du bloc.
 */
const measureBlock = (element) => {
  const origin = element.getBoundingClientRect().top;
  const bands = [];
  const lineHeights = new Map();
  const range = document.createRange();
  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);

  while (walker.nextNode()) {
    const textNode = walker.currentNode;
    if (!textNode.data) continue;

    const parent = textNode.parentElement;
    if (!lineHeights.has(parent)) {
      const value = parseFloat(getComputedStyle(parent).lineHeight);
      lineHeights.set(parent, Number.isFinite(value) ? value : null);
    }
    const parentLineHeight = lineHeights.get(parent);

    range.selectNodeContents(textNode);
    for (const rect of range.getClientRects()) {
      if (!rect.height) continue;
      const halfLeading = parentLineHeight ? (parentLineHeight - rect.height) / 2 : 0;
      const top = rect.top - origin - halfLeading;
      const bottom = rect.bottom - origin + halfLeading;
      if (bottom > top) bands.push([top, bottom]);
    }
  }

  element.querySelectorAll('img').forEach((img) => {
    const rect = img.getBoundingClientRect();
    bands.push([rect.top - origin, rect.bottom - origin]);
  });

  element.querySelectorAll('[data-pv-keep-top]').forEach((el) => {
    const rect = el.getBoundingClientRect();
    const style = getComputedStyle(el);
    const lead = parseFloat(style.borderTopWidth) + parseFloat(style.paddingTop);
    bands.push([rect.top - origin, rect.top - origin + lead + 1]);
  });

  bands.sort((a, b) => a[0] - b[0]);
  const merged = [];
  bands.forEach(([top, bottom]) => {
    const last = merged[merged.length - 1];
    if (last && top < last[1] - EPS) {
      last[1] = Math.max(last[1], bottom);
    } else {
      merged.push([top, bottom]);
    }
  });

  return { height: element.getBoundingClientRect().height, bands: merged };
};

/*
 * Répartit les blocs mesurés en pages de hauteur utile `available`.
 * Une page est une liste de fragments { index, offset, height } : la tranche
 * [offset, offset + height] du bloc `index`.
 */
const bandsAfter = (bands, offset) => bands.filter((band) => band[1] > offset + EPS);
const bandsEndingWithin = (bands, offset, space) => bands.filter((band) => band[1] - offset <= space + EPS);

// Contrôle des veuves et orphelines (actif par défaut dans Word) : jamais une ligne seule de part et d'autre d'une coupure
const linesKeptBeforeBreak = (fitting, remaining, isFirstFragment) => {
  const count = remaining - fitting === 1 ? fitting - 1 : fitting;
  return isFirstFragment && count < 2 ? 0 : count;
};

// Fin des lignes d'un bloc qui doivent rester sur la même page que sa première ligne
const firstLinesEnd = (block) => {
  const { bands } = block;
  if (!block.widowControl || bands.length < 2) return bands[0][1];
  return bands[bands.length <= 3 ? bands.length - 1 : 1][1];
};

const paginate = (blocks, available) => {
  const pages = [[]];
  let used = 0;

  const place = (index, offset, height) => {
    pages[pages.length - 1].push({ index, offset, height });
    used += height;
  };
  const newPage = () => {
    pages.push([]);
    used = 0;
  };

  blocks.forEach((block, index) => {
    let offset = 0;

    for (let guard = 0; guard < 1000; guard += 1) {
      const visible = bandsAfter(block.bands, offset);

      // En haut d'une page suivante, l'espace avant le bloc (ou le reste de son espace après) disparaît
      if (used === 0 && pages.length > 1) {
        if (!visible.length) return;
        offset = Math.max(offset, visible[0][0]);
      }

      if (!visible.length) {
        if (block.height > offset) place(index, offset, block.height - offset);
        return;
      }

      const space = available - used;
      const contentEnd = visible[visible.length - 1][1] - offset;

      if (contentEnd <= space + EPS) {
        const next = blocks[index + 1];
        if (block.keepNext && next && next.bands.length && used > 0) {
          const needed = block.height - offset + firstLinesEnd(next);
          if (needed > space + EPS) {
            newPage();
            continue;
          }
        }
        place(index, offset, block.height - offset);
        return;
      }

      const fitting = bandsEndingWithin(visible, offset, space);
      const kept = block.widowControl
        ? linesKeptBeforeBreak(fitting.length, visible.length, visible.length === block.bands.length)
        : fitting.length;
      if (!kept) {
        if (used > 0) {
          newPage();
          continue;
        }
        if (!fitting.length) {
          // Élément plus haut qu'une page entière : il est coupé au bas de la page
          place(index, offset, space);
          newPage();
          offset += space;
          continue;
        }
      }

      const cut = fitting[(kept || fitting.length) - 1][1];
      place(index, offset, cut - offset);
      newPage();
      offset = cut;
    }
  });

  return pages;
};

const readStoredZoom = () => {
  try {
    const stored = localStorage.getItem(ZOOM_STORAGE_KEY);
    if (stored && stored !== 'fit' && Number.isFinite(Number(stored))) return Number(stored);
  } catch {
    // localStorage indisponible : ajustement à la largeur
  }
  return 'fit';
};

function PvPreview({ header: headerNodes, body: bodyNodes, isDark, targetRubric, targetRubricTs, scrollToBottomTrigger, containerRef }) {
  const header = useMemo(() => collapseSpacing(headerNodes), [headerNodes]);
  const body = useMemo(() => collapseSpacing(bodyNodes), [bodyNodes]);
  const scrollRef = useRef(null);
  const setScrollElement = useCallback((element) => {
    scrollRef.current = element;
    if (containerRef) containerRef.current = element;
  }, [containerRef]);
  const headerMeasureRef = useRef(null);
  const bodyMeasureRef = useRef(null);
  const rubricRefs = useRef({});

  const [layout, setLayout] = useState(null);
  const [failedImages, setFailedImages] = useState(() => new Set());
  const [zoom, setZoom] = useState(readStoredZoom);
  const [fitScale, setFitScale] = useState(1);

  const onImageError = useCallback((src) => {
    setFailedImages((previous) => (previous.has(src) ? previous : new Set(previous).add(src)));
  }, []);

  const measure = useCallback(() => {
    const headerEl = headerMeasureRef.current;
    const bodyEl = bodyMeasureRef.current;
    if (!headerEl || !bodyEl) return;

    // Word descend le corps du texte si l'en-tête dépasse la marge du haut
    const headerHeight = headerEl.getBoundingClientRect().height;
    const bodyTop = PAGE.header + Math.max(PAGE.top - PAGE.header, headerHeight);
    const available = PAGE.height - PAGE.bottom - bodyTop;

    const blocks = Array.from(bodyEl.children).map((el, index) => ({
      ...measureBlock(el),
      keepNext: Boolean(body[index]?.keepNext),
      widowControl: body[index]?.type === 'paragraph',
    }));

    setLayout({ bodyTop, available, pages: paginate(blocks, available) });
  }, [body]);

  useLayoutEffect(() => {
    measure();
  }, [measure, header, failedImages]);

  useEffect(() => {
    let cancelled = false;
    document.fonts?.ready?.then(() => {
      if (!cancelled) measure();
    });
    return () => {
      cancelled = true;
    };
  }, [measure]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => {
      const width = el.clientWidth;
      if (width > 0) {
        setFitScale(Math.min(FIT_MAX, Math.max(0.3, (width - 2 * PAGE_GAP) / PAGE.width)));
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(ZOOM_STORAGE_KEY, String(zoom));
    } catch {
      // Préférence non conservée
    }
  }, [zoom]);

  useEffect(() => {
    if (targetRubric && rubricRefs.current[targetRubric]) {
      rubricRefs.current[targetRubric].scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [targetRubric, targetRubricTs]);

  useEffect(() => {
    if (scrollToBottomTrigger > 0 && scrollRef.current) {
      // Laisser le temps à la nouvelle rubrique d'être mise en page
      const timer = setTimeout(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
      }, 50);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [scrollToBottomTrigger]);

  const scale = zoom === 'fit' ? fitScale : zoom;
  const zoomIn = () => setZoom(ZOOM_STEPS.find((step) => step > scale + 0.001) ?? ZOOM_STEPS[ZOOM_STEPS.length - 1]);
  const zoomOut = () => setZoom([...ZOOM_STEPS].reverse().find((step) => step < scale - 0.001) ?? ZOOM_STEPS[0]);

  const pages = layout?.pages || [];
  const pageCount = pages.length;

  // Premier fragment de chaque bloc : cible du défilement vers une rubrique
  const firstFragments = useMemo(() => {
    const keys = new Set();
    const seen = new Set();
    (layout?.pages || []).forEach((fragments) => fragments.forEach((fragment) => {
      if (seen.has(fragment.index)) return;
      seen.add(fragment.index);
      keys.add(`${fragment.index}-${fragment.offset}`);
    }));
    return keys;
  }, [layout]);

  const measureCtx = useMemo(
    () => ({ page: { number: 1, count: 1 }, failedImages, onImageError }),
    [failedImages, onImageError]
  );

  const renderPage = (fragments, pageIndex) => {
    const ctx = { page: { number: pageIndex + 1, count: pageCount }, failedImages, onImageError };
    return (
      <Box
        key={pageIndex}
        sx={{
          width: PAGE.width * scale,
          height: PAGE.height * scale,
          flexShrink: 0,
          backgroundColor: '#fff',
          boxShadow: isDark
            ? '0 2px 12px rgba(0, 0, 0, 0.55)'
            : '0 1px 3px rgba(0, 0, 0, 0.18), 0 4px 14px rgba(0, 0, 0, 0.10)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            ...PAPER_TEXT,
            position: 'relative',
            width: PAGE.width,
            height: PAGE.height,
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
          }}
        >
          <div style={{ position: 'absolute', top: PAGE.header, left: PAGE.left, width: CONTENT_WIDTH }}>
            <PvNodes nodes={header} ctx={ctx} />
          </div>
          <div
            style={{
              position: 'absolute',
              top: layout.bodyTop,
              left: PAGE.left,
              width: CONTENT_WIDTH,
              height: layout.available,
              overflow: 'hidden',
            }}
          >
            {fragments.map((fragment) => {
              const node = body[fragment.index];
              if (!node) return null;
              const key = `${fragment.index}-${fragment.offset}`;
              const isRubricStart = Boolean(node.rubricId) && firstFragments.has(key);
              return (
                <div
                  key={key}
                  data-rubric-id={isRubricStart ? node.rubricId : undefined}
                  data-find-scope="content"
                  data-find-clip="true"
                  ref={isRubricStart ? (el) => { rubricRefs.current[node.rubricId] = el; } : undefined}
                  style={{ height: fragment.height, overflow: 'hidden', scrollMarginTop: 56 }}
                >
                  <div style={{ marginTop: -fragment.offset }}>
                    <PvNode node={node} ctx={ctx} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Box>
    );
  };

  return (
    <Box
      ref={setScrollElement}
      sx={{
        height: '100%',
        overflow: 'auto',
        scrollbarGutter: 'stable',
        backgroundColor: isDark ? '#1a252f' : '#e8eaed',
      }}
    >
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          left: 0,
          zIndex: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.5,
          px: 2,
          py: 0.5,
          backgroundColor: isDark ? 'rgb(38, 53, 65)' : '#f5f6f7',
          borderBottom: `1px solid ${isDark ? '#2d3f4c' : '#dadce0'}`,
          color: isDark ? '#ecf0f1' : 'text.secondary',
        }}
      >
        <Typography variant="caption" sx={{ mr: 1, color: 'inherit' }}>
          {pageCount} page{pageCount > 1 ? 's' : ''}
        </Typography>
        <Divider orientation="vertical" flexItem sx={{ mx: 1, borderColor: isDark ? '#2d3f4c' : undefined }} />
        <Tooltip title="Zoom arrière">
          <span>
            <IconButton size="small" onClick={zoomOut} disabled={scale <= ZOOM_STEPS[0] + 0.001} sx={{ color: 'inherit' }}>
              <ZoomOutIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Typography variant="caption" sx={{ minWidth: 40, textAlign: 'center', color: 'inherit' }}>
          {Math.round(scale * 100)} %
        </Typography>
        <Tooltip title="Zoom avant">
          <span>
            <IconButton
              size="small"
              onClick={zoomIn}
              disabled={scale >= ZOOM_STEPS[ZOOM_STEPS.length - 1] - 0.001}
              sx={{ color: 'inherit' }}
            >
              <ZoomInIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Ajuster à la largeur">
          <IconButton
            size="small"
            onClick={() => setZoom('fit')}
            sx={{ color: zoom === 'fit' ? (isDark ? '#4fc3f7' : 'primary.main') : 'inherit' }}
          >
            <FitScreenIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {layout && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: `${PAGE_GAP}px`,
            minWidth: PAGE.width * scale + 2 * PAGE_GAP,
            py: `${PAGE_GAP}px`,
          }}
        >
          {pages.map(renderPage)}
        </Box>
      )}

      {createPortal(
        <div
          aria-hidden="true"
          style={{
            ...PAPER_TEXT,
            position: 'fixed',
            top: 0,
            left: -100000,
            width: CONTENT_WIDTH,
            visibility: 'hidden',
            pointerEvents: 'none',
          }}
        >
          <div ref={headerMeasureRef}>
            <PvNodes nodes={header} ctx={measureCtx} />
          </div>
          <div ref={bodyMeasureRef}>
            {body.map((node, index) => (
              <div key={index}>
                <PvNode node={node} ctx={measureCtx} />
              </div>
            ))}
          </div>
        </div>,
        document.body
      )}
    </Box>
  );
}

export default PvPreview;
